import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { resolveEmail, buildSsoStartUrl, resolveOidcLoginHint } from "./ssoApi";
import PasswordSignIn from "./PasswordSignIn";
import { isEmailReadyForResolve } from "./emailResolveUtils";
import SSOLoginButton from "./idp/SSOLoginButton";
import { normalizeSsoProviders } from "./idp/normalizeSsoProvider";
import {
  resolveInitialAuthMode,
  shouldShowPasswordPanel,
  shouldShowSsoPanel,
} from "./authModeUtils";
import LoginExperienceSwitcher from "../login/LoginExperienceSwitcher";
import { takeForgotPasswordUsername } from "../forgotPassword/forgotPasswordSession";

/** @typedef {'identify' | 'resolving' | 'resolved'} FlowPhase */
/** @typedef {'sso' | 'password'} AuthenticationMode */

export default function EmailIdentify({
  initialUsername = "",
  urlError = "",
  onClearUrlError,
  onLoginSuccess,
  setIsForgotPassword,
  experience,
  onExperienceChange,
  showExperienceSwitcher = false,
  onPasswordResetRequired,
}) {
  const [username, setUsername] = useState(
    () => (initialUsername || "").trim() || takeForgotPasswordUsername()
  );
  const [touched, setTouched] = useState(false);
  const [discovery, setDiscovery] = useState({
    phase: "identify",
    result: null,
  });
  /** @type {[AuthenticationMode, React.Dispatch<React.SetStateAction<AuthenticationMode>>]} */
  const [authenticationMode, setAuthenticationMode] = useState("sso");
  const [passwordAutoFocus, setPasswordAutoFocus] = useState(false);
  const [modeAnnouncement, setModeAnnouncement] = useState("");
  const [redirectingProviderId, setRedirectingProviderId] = useState(null);
  const resolveSeq = useRef(0);
  const abortRef = useRef(null);
  const ssoPanelRef = useRef(null);

  const flowPhase = discovery.phase;
  const ssoResolved = discovery.result;

  useEffect(() => {
    if (urlError && /unable to sign in with this email/i.test(urlError)) {
      onClearUrlError?.();
    }
  }, [urlError, onClearUrlError]);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  const trimmed = username.trim();
  const usernameError = touched && trimmed.length === 0;

  const displayUrlError =
    urlError && !/unable to sign in with this email/i.test(urlError)
      ? urlError
      : "";

  const discoveryComplete = flowPhase === "resolved";
  const discoveryLoading = flowPhase === "resolving";

  const normalizedProviders = normalizeSsoProviders(ssoResolved?.providers);

  const showSso = Boolean(
    discoveryComplete &&
      ssoResolved?.ssoSignInEnabled &&
      normalizedProviders.length
  );
  const showPasswordEnabled = Boolean(
    discoveryComplete && (ssoResolved?.passwordSignInEnabled ?? true)
  );
  const hidePasswordEnforced = Boolean(
    discoveryComplete &&
      ssoResolved?.enforce_sso &&
      ssoResolved?.ssoSignInEnabled &&
      !ssoResolved?.passwordSignInEnabled
  );

  const showSsoPanel = shouldShowSsoPanel({
    discoveryComplete,
    showSso,
    authenticationMode,
  });
  const showPasswordPanel = shouldShowPasswordPanel({
    discoveryComplete,
    authenticationMode,
    showPasswordEnabled,
    hidePasswordEnforced,
  });

  const usernameLocked = Boolean(discoveryComplete);

  const applyDiscoveryResult = useCallback((data) => {
    const mode = resolveInitialAuthMode(data);
    setDiscovery({ phase: "resolved", result: data || null });
    setAuthenticationMode(mode);
    setPasswordAutoFocus(mode === "password");
    setModeAnnouncement("");
  }, []);

  const runResolve = useCallback(
    async (email, seq) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const data = await resolveEmail(email, { signal: controller.signal });
        if (seq !== resolveSeq.current) return;
        applyDiscoveryResult(data);
      } catch (err) {
        if (err?.name === "AbortError" || err?.aborted) return;
        if (seq !== resolveSeq.current) return;
        applyDiscoveryResult(null);
      }
    },
    [applyDiscoveryResult]
  );

  const resetDiscovery = () => {
    abortRef.current?.abort();
    resolveSeq.current += 1;
    setDiscovery({ phase: "identify", result: null });
    setAuthenticationMode("sso");
    setPasswordAutoFocus(false);
    setModeAnnouncement("");
    setRedirectingProviderId(null);
  };

  const switchToPasswordMode = () => {
    setAuthenticationMode("password");
    setPasswordAutoFocus(true);
    setModeAnnouncement("Password sign-in mode. Enter your password below.");
  };

  const switchToSsoMode = () => {
    setAuthenticationMode("sso");
    setPasswordAutoFocus(false);
    setModeAnnouncement(
      "Single sign-on mode. Choose your organization sign-in option."
    );
    requestAnimationFrame(() => {
      ssoPanelRef.current?.querySelector("button")?.focus();
    });
  };

  const handleUsernameChange = (value) => {
    setUsername(value);
    setTouched(true);
    onClearUrlError?.();
    if (flowPhase !== "identify") {
      resetDiscovery();
    }
  };

  const handleContinue = () => {
    setTouched(true);
    if (!trimmed) return;

    onClearUrlError?.();
    setRedirectingProviderId(null);

    if (!isEmailReadyForResolve(trimmed)) {
      applyDiscoveryResult(null);
      return;
    }

    const seq = ++resolveSeq.current;
    setDiscovery({ phase: "resolving", result: null });
    runResolve(trimmed, seq);
  };

  const handleUsernameKeyDown = (e) => {
    if (e.key === "Enter" && flowPhase === "identify") {
      e.preventDefault();
      handleContinue();
    }
  };

  const startSso = (provider) => {
    if (!provider?.id || redirectingProviderId != null) {
      return;
    }

    setRedirectingProviderId(provider.id);
    window.location.href = buildSsoStartUrl(
      ssoResolved.user_pattern,
      provider.id,
      provider.protocol,
      trimmed,
      resolveOidcLoginHint(ssoResolved, trimmed)
    );
  };

  return (
    <Box>
      <Box
        component="span"
        sx={{
          position: "absolute",
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: "hidden",
          clip: "rect(0, 0, 0, 0)",
          whiteSpace: "nowrap",
          border: 0,
        }}
        aria-live="polite"
        aria-atomic="true"
      >
        {modeAnnouncement}
      </Box>

      {displayUrlError && (
        <Alert severity="error" sx={{ mb: 2 }} variant="outlined">
          {displayUrlError}
        </Alert>
      )}

      <TextField
        id="login-username"
        name="username"
        label="Username"
        type="text"
        fullWidth
        margin="normal"
        value={username}
        onChange={(e) => handleUsernameChange(e.target.value)}
        onKeyDown={handleUsernameKeyDown}
        error={usernameError}
        helperText={usernameError ? "Please enter a valid username" : ""}
        autoComplete="username"
        disabled={discoveryLoading || redirectingProviderId != null}
        slotProps={{
          input: {
            readOnly: usernameLocked,
            "data-testid": "username-input",
          },
        }}
        InputLabelProps={{ shrink: true }}
      />

      {flowPhase === "identify" && (
        <Button
          type="button"
          variant="contained"
          color="primary"
          fullWidth
          className="button"
          onClick={handleContinue}
          sx={{ mt: 3 }}
          data-testid="continue-button"
        >
          Continue
        </Button>
      )}

      {flowPhase === "identify" &&
        showExperienceSwitcher &&
        experience &&
        onExperienceChange && (
          <LoginExperienceSwitcher
            experience={experience}
            onChange={onExperienceChange}
          />
        )}

      {discoveryLoading && (
        <Box
          sx={{ mt: 3, display: "flex", alignItems: "center", gap: 1.5 }}
          data-testid="discovery-loading"
          role="status"
          aria-live="polite"
        >
          <CircularProgress size={22} color="primary" aria-hidden="true" />
          <Typography variant="body2" color="text.secondary">
            Checking organization sign-in options…
          </Typography>
        </Box>
      )}

      <Collapse in={showSsoPanel} unmountOnExit timeout={220}>
        <Stack
          ref={ssoPanelRef}
          spacing={2}
          sx={{ my: 2 }}
          data-testid="sso-auth-panel"
        >
          {normalizedProviders.map((provider) => (
            <SSOLoginButton
              key={provider.id}
              provider={provider.provider}
              protocol={provider.protocol}
              displayName={provider.displayName}
              buttonLabel={provider.buttonLabel}
              logoUrl={provider.logoUrl}
              brandColor={provider.brandColor}
              loading={redirectingProviderId === provider.id}
              disabled={redirectingProviderId != null}
              onClick={() => startSso(provider)}
              testId={`sso-provider-${provider.id}`}
            />
          ))}
          {showPasswordEnabled && (
            <Box
              sx={{ pt: 1, borderTop: "1px solid rgba(255, 255, 255, 0.12)" }}
            >
              <Link
                component="button"
                type="button"
                color="primary"
                className="forgot-password-btn"
                onClick={switchToPasswordMode}
                data-testid="continue-password-link"
              >
                Or use your username and password
              </Link>
            </Box>
          )}
        </Stack>
      </Collapse>

      <Collapse in={showPasswordPanel} unmountOnExit timeout={220}>
        <Box data-testid="password-auth-panel">
          <PasswordSignIn
            username={trimmed}
            resolved={ssoResolved}
            onLoginSuccess={onLoginSuccess}
            setIsForgotPassword={setIsForgotPassword}
            autoFocus={passwordAutoFocus}
            onPasswordResetRequired={onPasswordResetRequired}
          />

          {showSso && (
            <Link
              component="button"
              type="button"
              color="primary"
              className="forgot-password-btn"
              onClick={switchToSsoMode}
              sx={{ mt: 2, display: "inline-block" }}
              data-testid="back-to-sso-link"
            >
              Back to Single Sign-On
            </Link>
          )}
        </Box>
      </Collapse>

      {discoveryComplete && (showSsoPanel || showPasswordPanel) && (
        <Link
          component="button"
          type="button"
          color="primary"
          className="forgot-password-btn"
          onClick={resetDiscovery}
          sx={{ mt: 2, display: "inline-block" }}
          data-testid="use-different-email-link"
        >
          Use a different username
        </Link>
      )}
    </Box>
  );
}
